// src/platform/ui/splashScreen.js

import { ref, readonly } from 'vue'

import configuration from '@/platform/core/configuration'
import eventBus from '@/platform/core/eventBus'
import logger from '@/platform/core/logger'


class SplashScreen {
  constructor() {
    this.visible = ref(true)

    this.message = ref(
      'Loading NexgbitsIS...'
    )

    this.progress = ref(null)

    this.initialized = false

    this.minimumDisplayTimer = null
    this.hideWaitTimer = null

    this.minimumDisplayComplete = false
  }


  init(options = {}) {
    if (this.initialized) {
      return
    }

    const configuredMinimumDisplayMs =
      configuration.get(
        'ui.splash.minimumDisplayMs',
        500
      )

    const minimumDisplayMs =
      typeof options.minimumDisplayMs ===
      'number'
        ? options.minimumDisplayMs
        : configuredMinimumDisplayMs

    const configuredMessage =
      configuration.get(
        'ui.splash.message',
        'Loading NexgbitsIS...'
      )

    this.message.value =
      options.message ||
      configuredMessage

    this.progress.value =
      null

    this.visible.value =
      true

    this.minimumDisplayComplete =
      false


    if (
      typeof window !== 'undefined'
    ) {
      this.minimumDisplayTimer =
        window.setTimeout(
          () => {
            this.minimumDisplayComplete =
              true

            this.minimumDisplayTimer =
              null

            eventBus.emit(
              'platform.splash.minimum.completed',
              {
                timestamp:
                  new Date().toISOString()
              }
            )
          },
          minimumDisplayMs
        )
    } else {
      this.minimumDisplayComplete =
        true
    }


    this.initialized =
      true


    logger.info(
      'Splash screen initialized.',
      {
        minimumDisplayMs
      }
    )


    eventBus.emit(
      'platform.splash.shown',
      {
        message:
          this.message.value,

        timestamp:
          new Date().toISOString()
      }
    )
  }


  show(message = null) {
    if (message) {
      this.message.value =
        message
    }

    this.visible.value =
      true

    logger.debug(
      'Splash screen shown.',
      {
        message:
          this.message.value
      }
    )

    eventBus.emit(
      'platform.splash.shown',
      {
        message:
          this.message.value,

        timestamp:
          new Date().toISOString()
      }
    )
  }


  hide(force = false) {
    if (force) {
      this.completeHide(
        'forced'
      )

      return true
    }


    if (
      this.minimumDisplayComplete
    ) {
      this.completeHide(
        'completed'
      )

      return true
    }


    /*
     * Prevent multiple hide() calls from creating
     * multiple polling timers.
     */
    if (this.hideWaitTimer) {
      return true
    }


    if (
      typeof window === 'undefined'
    ) {
      this.completeHide(
        'completed'
      )

      return true
    }


    this.hideWaitTimer =
      window.setInterval(
        () => {
          if (
            !this.minimumDisplayComplete
          ) {
            return
          }

          window.clearInterval(
            this.hideWaitTimer
          )

          this.hideWaitTimer =
            null

          this.completeHide(
            'completed'
          )
        },
        50
      )


    return true
  }


  completeHide(reason) {
    if (!this.visible.value) {
      return
    }

    this.visible.value =
      false

    logger.debug(
      'Splash screen hidden.',
      {
        reason
      }
    )

    eventBus.emit(
      'platform.splash.hidden',
      {
        reason,

        timestamp:
          new Date().toISOString()
      }
    )
  }


  setMessage(message) {
    this.message.value =
      message || ''

    eventBus.emit(
      'platform.splash.message.changed',
      {
        message:
          this.message.value
      }
    )
  }


  setProgress(value) {
    if (
      value === null ||
      value === undefined
    ) {
      this.progress.value =
        null

      eventBus.emit(
        'platform.splash.progress.changed',
        {
          progress:
            null
        }
      )

      return
    }


    const numericValue =
      Number(value)


    if (
      Number.isNaN(
        numericValue
      )
    ) {
      logger.warn(
        'Invalid splash progress value ignored.',
        {
          value
        }
      )

      return
    }


    this.progress.value =
      Math.min(
        100,
        Math.max(
          0,
          numericValue
        )
      )


    eventBus.emit(
      'platform.splash.progress.changed',
      {
        progress:
          this.progress.value
      }
    )
  }


  reset() {
    this.message.value =
      configuration.get(
        'ui.splash.message',
        'Loading NexgbitsIS...'
      )

    this.progress.value =
      null

    eventBus.emit(
      'platform.splash.reset',
      {
        message:
          this.message.value
      }
    )
  }


  destroy() {
    if (
      typeof window !== 'undefined'
    ) {
      if (
        this.minimumDisplayTimer
      ) {
        window.clearTimeout(
          this.minimumDisplayTimer
        )

        this.minimumDisplayTimer =
          null
      }


      if (
        this.hideWaitTimer
      ) {
        window.clearInterval(
          this.hideWaitTimer
        )

        this.hideWaitTimer =
          null
      }
    }


    this.visible.value =
      false

    this.initialized =
      false

    this.minimumDisplayComplete =
      false


    logger.info(
      'Splash screen destroyed.'
    )


    eventBus.emit(
      'platform.splash.destroyed',
      {
        timestamp:
          new Date().toISOString()
      }
    )
  }


  getState() {
    return {
      visible:
        readonly(
          this.visible
        ),

      message:
        readonly(
          this.message
        ),

      progress:
        readonly(
          this.progress
        )
    }
  }
}


const splashScreen =
  new SplashScreen()


export default splashScreen